import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid2,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Paper,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  getFacilitiyV2,
  getSectorV2,
  getStatePersonalizedV2,
  getFacilitiyCategory,
} from '../../../helpers/api';
import LoaderSkeleton from '../../common/LoaderSkeleton';
import { Empty } from 'antd';
import MUIDataTable from 'mui-datatables';
import AppPagination from '../../common/AppPagination';
import { properCase } from '../../../helpers/utils';
import {
  Add,
  Close,
  EditOutlined,
  EventSeatOutlined,
  FilterAltOutlined,
  InfoOutlined,
  KeyboardArrowRight,
  MoreHoriz,
  Payment,
  PieChartOutline,
  Search,
  BusinessRounded,
  LocationOnOutlined,
  CalendarTodayOutlined,
} from '@mui/icons-material';
import FacilityReportQuarters from './facility-result-quarters/FacilityReportQuarters';
import { withRouter } from 'react-router';
import { userCats } from '../../../helpers/appstate';
import { openNotification } from '../../../helpers/notification';
import AcceptToJoinFacility from './join-facility/AcceptToJoinFacility';

const FacilityScreen = withRouter(({ props, history }) => {
  const { userCategory, userPermission, userId } = props;
  // Personnel and consultants don't filter by state (and can't load the state list)
  const canFilterByState =
    userCategory !== userCats.PERSONNEL && userCategory !== userCats.CONSULTANT;
  const userPerm = userPermission?.map((i) => i?.trim()?.toLowerCase());

  const canCreateStandardReport = userPerm.includes('record_report');
  const canLockFacilityReport = userPerm.includes('schedule_quarter');
  const canUpdateFacility = userPerm.includes('update_facility');
  const canApprovePayment =
    userPerm.includes('approve_payment') ||
    userPerm.includes('view_payment') ||
    userPerm.includes('view_invoice');
  const canViewInvoiceAndPayment =
    userPerm.includes('view_payment') || userPerm.includes('view_invoice');
  const canCreateFacility = userPerm.includes('create_facility');

  const statuses = ['ACTIVE', 'INACTIVE'];

  const initState = { stateId: '', stateName: '' };
  const initSector = { sectorId: '', sectorName: '' };
  const initCategory = { categoryId: '', categoryName: '' };

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [row, setRow] = useState('');
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pageNum, setPageNum] = useState(1);
  const pageSize = 10;
  const [search, setSearch] = useState('');
  const [searchParam, setSearchParam] = useState('');

  // Applied filters that trigger API calls
  const [selectedStatus, setSelectedStatus] = useState('');
  const [stateSelected, setStateSelected] = useState(initState);

  // Temporary filters holding selection inside dropdown
  const [tempStatus, setTempStatus] = useState('');
  const [tempState, setTempState] = useState(initState);
  const [tempSector, setTempSector] = useState(initSector);
  const [tempCategory, setTempCategory] = useState(initCategory);

  const [anchorOverflowMenuEl, setAnchorOverflowMenuEl] = useState(null);
  const [anchorFilterDrowpdownEl, setAnchorFilterDropdownMenuEl] = useState(null);
  const [dialogQuarters, setDialogQuarters] = useState(false);
  const [dialogAcceptJoin, setDialogAcceptJoin] = useState(false);
  const [stateList, setStateList] = useState([]);
  const [stateLoading, setStateLoading] = useState(false);
  const [sectorList, setSectorList] = useState([]);
  const [sectorLoading, setSectorLoading] = useState(false);
  const [sectorSelected, setSectorSelected] = useState(initSector);
  const [categoryList, setCategoryList] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categorySelected, setCategorySelected] = useState(initCategory);

  useEffect(() => {
    fetchData();
  }, [search, pageNum, total, selectedStatus, stateSelected, sectorSelected, categorySelected]);

  useEffect(() => {
    if (refresh) fetchData();
  }, [refresh]);

  useEffect(() => {
    if (canFilterByState) fetchStateList();
    fetchSectorList();
    fetchCategoryList();
  }, []);

  const handleOverflowMenuClick = (event) => setAnchorOverflowMenuEl(event.currentTarget);
  const handleOverflowMenuClose = () => setAnchorOverflowMenuEl(null);

  const handleFilterDropdownClick = (event) => {
    // Sync temp states with current applied states when opening
    setTempStatus(selectedStatus);
    setTempState(stateSelected);
    setTempSector(sectorSelected);
    setTempCategory(categorySelected);
    setAnchorFilterDropdownMenuEl(event.currentTarget);
  };
  const handleFilterDropdownClose = () => setAnchorFilterDropdownMenuEl(null);

  const handleApplyFilters = () => {
    setSelectedStatus(tempStatus);
    setStateSelected(tempState);
    setSectorSelected(tempSector);
    setCategorySelected(tempCategory);
    setPageNum(1); // Reset to first page on new filter application
    handleFilterDropdownClose();
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await getFacilitiyV2(
        search,
        selectedStatus,
        '',
        '',
        stateSelected?.stateId || '',
        sectorSelected?.sectorId || '',
        '',
        categorySelected?.categoryId || '',
        pageNum,
        pageSize
      );
      const { data, totalCount, status } = res?.result;
      if (status?.code === '00') {
        if (data?.length === 1 && userCategory === userCats.PERSONNEL) {
          history.push(`/facilities/${data[0]?.facilityId}/view`);
        } else {
          setTotal(totalCount);
          setData(data);
        }
      } else {
        openNotification({ type: 'error', message: status?.desc });
      }
    } catch (error) {
      const errmsg = error?.data?.result?.data?.status?.desc || 'Error fetching data';
      openNotification({ type: 'error', message: errmsg });
    } finally {
      setLoading(false);
      setRefresh(false);
    }
  };

  const fetchStateList = async () => {
    setStateLoading(true);
    try {
      const res = await getStatePersonalizedV2();
      if (res?.result?.status?.code === '00') setStateList(res?.result?.data || []);
    } catch (error) {
      // Only feeds the State filter; leave it empty rather than showing an error
      setStateList([]);
    } finally {
      setStateLoading(false);
    }
  };

  const fetchSectorList = async () => {
    setSectorLoading(true);
    try {
      const res = await getSectorV2();
      if (res?.result?.status?.code === '00') setSectorList(res?.result?.data || []);
    } catch (error) {
      openNotification({ type: 'error', message: 'Error loading sectors' });
    } finally {
      setSectorLoading(false);
    }
  };

  const fetchCategoryList = async () => {
    setCategoryLoading(true);
    try {
      const res = await getFacilitiyCategory();
      if (res?.result?.status?.code === '00') setCategoryList(res?.result?.data || []);
    } catch (error) {
      setCategoryLoading({ type: 'error', message: 'Error loading categories' });
    } finally {
      setSectorLoading(false);
    }
  };

  const getCategoryTheme = (category) => {
    switch (category) {
      case 'SUPER':
        return { color: '#d32f2f', bg: alpha('#d32f2f', 0.1) };
      case 'LARGE':
        return { color: '#ed6c02', bg: alpha('#ed6c02', 0.1) };
      case 'MEDUIM':
        return { color: '#0288d1', bg: alpha('#0288d1', 0.1) };
      case 'SMALL':
        return { color: '#2e7d32', bg: alpha('#2e7d32', 0.1) };
      case 'MINI':
        return { color: '#9c27b0', bg: alpha('#9c27b0', 0.1) };
      default:
        return { color: '#757575', bg: '#f5f5f5' };
    }
  };

  const renderDisplay = () => {
    const columns = [
      {
        name: 'facilityName',
        label: 'Facility Details',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const item = data[index];
            return (
              <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 0.5 }}>
                <Box sx={{ maxWidth: '250px' }}>
                  <Tooltip title={item?.facilityName}>
                    <Typography variant="body2" fontWeight={700} noWrap color="text.primary">
                      {item?.facilityName}
                    </Typography>
                  </Tooltip>
                  <Typography variant="caption" color="textSecondary" fontWeight={500}>
                    {`${item?.samplePointCount} Sample Points`}
                  </Typography>
                </Box>
              </Stack>
            );
          },
        },
      },
      {
        name: 'facilityCategoryName',
        label: 'Tier',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const cat = data[index]?.facilityCategoryName;
            const theme = getCategoryTheme(cat);
            return (
              <Chip
                label={properCase(cat)}
                size="small"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  color: theme.color,
                  bgcolor: theme.bg,
                  borderRadius: '6px',
                  minWidth: '70px',
                  height: 24,
                }}
              />
            );
          },
        },
      },
      {
        name: 'sectorName',
        label: 'Sector',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => (
            <Typography variant="body2" fontWeight={500} color="text.secondary">
              {data[index]?.sectorName}
            </Typography>
          ),
        },
      },
      {
        name: 'stateName',
        label: 'State',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => (
            <Typography variant="body2" fontWeight={700} color="text.primary">
              {data[index]?.stateName}
            </Typography>
          ),
        },
      },
      {
        name: 'entryDate',
        label: 'Joined',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => (
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {new Date(data[index].entryDate).toLocaleDateString('en-NG', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </Typography>
          ),
        },
      },
      {
        name: 'status',
        label: 'Status',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const status = data[index]?.status;
            const isActive = status === 'ACTIVE';
            return (
              <Chip
                label={properCase(status)}
                size="small"
                sx={{
                  height: 24,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  bgcolor: isActive ? alpha('#2e7d32', 0.1) : alpha('#ed6c02', 0.1),
                  color: isActive ? '#2e7d32' : '#ed6c02',
                  border: '1px solid',
                  borderColor: 'inherit',
                }}
              />
            );
          },
        },
      },
      {
        name: '',
        label: 'Actions',
        options: {
          customHeadLabelRender: (a) => (
            <Typography
              variant="button"
              fontWeight={700}
              color="text.secondary"
              sx={{ fontSize: '0.75rem' }}
              align="center"
            >
              {a.label}
            </Typography>
          ),
          customBodyRenderLite: (index) => {
            const record = data[index];
            const consultantToAccept = record?.facilityConsultant?.some(
              (x) => x.userId === userId && x.status === 'PENDING' && x.inducedBy === 'FACILITY'
            );

            if (consultantToAccept) {
              return (
                <Button
                  size="small"
                  variant="contained"
                  sx={{ fontSize: '0.75rem', textTransform: 'none', borderRadius: '8px', px: 2 }}
                  onClick={() => {
                    setRow(record);
                    setDialogAcceptJoin(true);
                  }}
                >
                  Accept
                </Button>
              );
            } else {
              return (
                <Box align="center">
                  <IconButton
                    size="small"
                    sx={{ bgcolor: alpha('#000', 0.03) }}
                    onClick={(e) => {
                      handleOverflowMenuClick(e);
                      setRow(record);
                    }}
                  >
                    <MoreHoriz />
                  </IconButton>
                </Box>
              );
            }
          },
        },
      },
    ];

    return (
      <MUIDataTable
        data={data}
        columns={columns}
        options={{
          elevation: 0,
          search: false,
          filter: false,
          download: false,
          print: false,
          viewColumns: false,
          selectableRows: 'none',
          responsive: 'standard',
          pagination: false,
          setRowProps: (ev, dataIndex) => ({
            style: { backgroundColor: dataIndex % 2 === 0 ? '#ffffff' : alpha('#f5f7ff', 0.5) },
          }),
          setTableProps: () => ({ size: 'small' }),
        }}
      />
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#F4F6F8', minHeight: '100vh', mt: -1 }}>
      {/* PROFESSIONAL ACTIONS HEADER */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: '16px',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: '#fff',
        }}
      >
        <Box
          display="flex"
          flexWrap="wrap"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            {userCategory === userCats.EDM && canCreateFacility && (
              <Button
                variant="contained"
                disableElevation
                startIcon={<Add />}
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 3 }}
                onClick={() => history.push('/facilities/create')}
              >
                New Facility
              </Button>
            )}
            {userCategory === userCats.CONSULTANT && (
              <Button
                variant="contained"
                disableElevation
                startIcon={<Add />}
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 3 }}
                onClick={() => history.push('/facilities/join')}
              >
                Join a Facility
              </Button>
            )}
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            {selectedStatus && (
              <Chip
                label={`Status: ${properCase(selectedStatus)}`}
                onDelete={() => setSelectedStatus('')}
                sx={{
                  fontWeight: 700,
                  borderRadius: '8px',
                  bgcolor: alpha('#1976d2', 0.05),
                  color: 'primary.main',
                }}
              />
            )}
            {stateSelected?.stateId && (
              <Chip
                label={`State: ${stateSelected.stateName}`}
                onDelete={() => setStateSelected(initState)}
                sx={{
                  fontWeight: 700,
                  borderRadius: '8px',
                  bgcolor: alpha('#9c27b0', 0.05),
                  color: 'secondary.main',
                }}
              />
            )}
            {sectorSelected?.sectorId && (
              <Chip
                label={`Sector: ${sectorSelected.sectorName}`}
                onDelete={() => setSectorSelected(initSector)}
                sx={{
                  fontWeight: 700,
                  borderRadius: '8px',
                  bgcolor: alpha('#9c27b0', 0.05),
                  color: 'secondary.main',
                }}
              />
            )}
            {categorySelected?.categoryId && (
              <Chip
                label={`Category: ${categorySelected.categoryName}`}
                onDelete={() => setCategorySelected(initCategory)}
                sx={{
                  fontWeight: 700,
                  borderRadius: '8px',
                  bgcolor: alpha('#9c27b0', 0.05),
                  color: 'secondary.main',
                }}
              />
            )}
            <TextField
              value={searchParam}
              placeholder="Search facility name..."
              size="small"
              onChange={(e) => setSearchParam(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && setSearch(searchParam)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
                sx: { borderRadius: '12px', bgcolor: '#f9fafb', width: '280px' },
              }}
            />
            <Tooltip title="Filter Settings">
              <IconButton
                onClick={handleFilterDropdownClick}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px' }}
              >
                <FilterAltOutlined />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </Paper>

      {/* READABLE DATA TABLE */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '16px',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Box sx={{ p: 4 }}>
            <LoaderSkeleton />
            <Divider sx={{ my: 3 }} />
            <LoaderSkeleton />
          </Box>
        ) : data.length < 1 ? (
          <Box sx={{ py: 10, textAlign: 'center' }}>
            <Empty description="No facilities found matching your criteria" />
          </Box>
        ) : (
          renderDisplay()
        )}
      </Paper>

      {/* FOOTER PAGINATION */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="textSecondary" fontWeight={500}>
          Showing results {(pageNum - 1) * pageSize + 1} - {Math.min(pageNum * pageSize, total)} of{' '}
          {total}
        </Typography>
        <AppPagination
          defaultCurrent={1}
          defaultPageSize={pageSize}
          total={total}
          onPageChange={(p) => setPageNum(p)}
        />
      </Box>

      {/* --- OVERLAYS & MENUS --- */}
      <Menu
        anchorEl={anchorFilterDrowpdownEl}
        open={Boolean(anchorFilterDrowpdownEl)}
        onClose={handleFilterDropdownClose}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            mt: 1,
            minWidth: 280,
            p: 2.5,
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          },
        }}
      >
        <Typography
          variant="overline"
          sx={{ fontWeight: 700, color: 'text.disabled', display: 'block', mb: 0.5 }}
        >
          Filters
        </Typography>

        <Stack spacing={2.5}>
          <Box>
            <TextField
              select
              fullWidth
              size="small"
              value={tempStatus}
              onChange={(e) => setTempStatus(e.target.value)}
              SelectProps={{ native: true }}
              InputProps={{ sx: { borderRadius: '8px' } }}
            >
              <option value="">All Statuses</option>
              {statuses.map((item) => (
                <option key={item} value={item}>
                  {properCase(item)}
                </option>
              ))}
            </TextField>
          </Box>

          {canFilterByState && (
          <Box>
            <Autocomplete
              options={stateList || []}
              loading={stateLoading}
              getOptionLabel={(option) => option?.stateName || ''}
              value={tempState?.stateId ? tempState : null}
              onChange={(event, newValue) => {
                setTempState(newValue || initState);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select State"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    sx: { borderRadius: '8px' },
                  }}
                />
              )}
            />
          </Box>
          )}

          <Box>
            <Autocomplete
              options={sectorList || []}
              loading={sectorLoading}
              getOptionLabel={(option) => option?.sectorName || ''}
              value={tempSector?.sectorId ? tempSector : null}
              onChange={(event, newValue) => {
                setTempSector(newValue || initSector);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Sector"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    sx: { borderRadius: '8px' },
                  }}
                />
              )}
            />
          </Box>

          <Box>
            <Autocomplete
              options={categoryList || []}
              loading={categoryLoading}
              getOptionLabel={(option) => option?.categoryName || ''}
              value={tempCategory?.categoryId ? tempCategory : null}
              onChange={(event, newValue) => {
                setTempCategory(newValue || initCategory);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Category"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    sx: { borderRadius: '8px' },
                  }}
                />
              )}
            />
          </Box>

          <Button
            variant="contained"
            fullWidth
            size="small"
            onClick={handleApplyFilters}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, mt: 1 }}
          >
            Apply Filters
          </Button>
        </Stack>
      </Menu>

      <Menu
        anchorEl={anchorOverflowMenuEl}
        open={Boolean(anchorOverflowMenuEl)}
        onClose={handleOverflowMenuClose}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            mt: 1,
            width: 260,
            boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
            border: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <ListItemButton
          sx={{ py: 1.5 }}
          onClick={() => {
            handleOverflowMenuClose();
            history.push(`/facilities/${row?.facilityId}/view`);
          }}
        >
          <ListItemIcon>
            <InfoOutlined color="primary" />
          </ListItemIcon>
          <ListItemText
            primary="View Facility Profile"
            primaryTypographyProps={{ fontWeight: 700, variant: 'body2' }}
          />
        </ListItemButton>
        <Divider />
        {(userCategory === userCats.EDM || userCategory === userCats.CONSULTANT) &&
          canCreateStandardReport && (
            <ListItemButton
              sx={{ py: 1.5 }}
              onClick={() => {
                handleOverflowMenuClose();
                history.push(`/facilities/${row?.facilityId}/quarters`);
              }}
            >
              <ListItemIcon>
                <PieChartOutline />
              </ListItemIcon>
              <ListItemText
                primary="Standard Monitoring Results"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
              />
            </ListItemButton>
          )}
        {canLockFacilityReport && (
          <ListItemButton
            sx={{ py: 1.5 }}
            onClick={() => {
              handleOverflowMenuClose();
              setDialogQuarters(true);
            }}
          >
            <ListItemIcon>
              <EventSeatOutlined />
            </ListItemIcon>
            <ListItemText
              primary="Reserve QAQC Quarter"
              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
            />
          </ListItemButton>
        )}
        {userCategory === userCats.EDM && (canViewInvoiceAndPayment || canApprovePayment) && (
          <ListItemButton
            sx={{ py: 1.5 }}
            onClick={() => {
              handleOverflowMenuClose();
              history.push(`/facilities/invoice/${row?.facilityId}`);
            }}
          >
            <ListItemIcon>
              <Payment />
            </ListItemIcon>
            <ListItemText
              primary="Billing & Payments"
              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
            />
          </ListItemButton>
        )}
        {userCategory === userCats.EDM && canUpdateFacility && (
          <ListItemButton
            sx={{ py: 1.5, bgcolor: alpha('#1976d2', 0.05) }}
            onClick={() => {
              handleOverflowMenuClose();
              history.push(`/facilities/${row?.facilityId}/edit`);
            }}
          >
            <ListItemIcon>
              <EditOutlined color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Modify Facility Data"
              primaryTypographyProps={{ variant: 'body2', fontWeight: 700, color: 'primary' }}
            />
          </ListItemButton>
        )}
      </Menu>

      {dialogQuarters && (
        <FacilityReportQuarters
          open={dialogQuarters}
          onClose={() => setDialogQuarters(false)}
          record={row}
        />
      )}
      {dialogAcceptJoin && (
        <AcceptToJoinFacility
          open={dialogAcceptJoin}
          onClose={() => {
            setDialogAcceptJoin(false);
            setRefresh(true);
          }}
          facility={row}
        />
      )}
    </Box>
  );
});

export default FacilityScreen;
